import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaInstagram,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa6";
import { FaClock, FaWhatsapp } from "react-icons/fa";
import { indiaSupport } from "../../content/indiaSiteContent";

export const socialLinks = {
  instagram: "https://www.instagram.com/get2vacationsindia/",
  facebook: "https://www.facebook.com/Get2VacationsIndia/",
} as const;

const quickLinks = [
  { label: "About Us", path: "/about-us" },
  { label: "Destinations", path: "/destination" },
  { label: "Visa Services", path: "/visa-services" },
  { label: "Support", path: "/help-and-support" },
];

const Footer: React.FC = () => {
  const { t } = useTranslation();

  return (
    <footer
      id="footer"
      className="border-t border-white/10 bg-brand-dark pb-10 pt-20"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <h2 className="mb-6 font-heading text-2xl font-bold tracking-wider text-white">
              GET2<span className="text-brand-pink">VACATIONS</span>
            </h2>

            <p className="mb-6 text-sm leading-relaxed text-gray-400">
              {t(
                "GET2VACATIONS helps Indian travellers plan international holidays with destination guidance, visa support, and responsive customer assistance.",
              )}
            </p>

            <div className="flex gap-4">
              {[
                { Icon: FaInstagram, href: socialLinks.instagram },
                { Icon: FaFacebookF, href: socialLinks.facebook },
                { Icon: FaTwitter, href: "#" },
                { Icon: FaLinkedinIn, href: "#" },
              ].map(({ Icon, href }, index) => (
                <a
                  key={index}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-gray-300 transition-colors hover:bg-brand-pink hover:text-white"
                >
                  <Icon />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="mb-6 text-sm font-semibold uppercase tracking-wider text-white">
              {t("Quick Links")}
            </h4>

            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 transition-colors hover:text-brand-pink"
                  >
                    {t(link.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-6 text-sm font-semibold uppercase tracking-wider text-white">
              {t("Contact Us")}
            </h4>

            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <FaWhatsapp className="text-brand-pink" />

                <a
                  href={indiaSupport.whatsappHref}
                  className="text-sm text-gray-400 transition-colors hover:text-brand-pink"
                >
                  {indiaSupport.whatsappDisplay}
                </a>
              </li>

              <li className="flex items-center gap-3">
                <FaPhone className="text-brand-pink" />

                <a
                  href={indiaSupport.phoneHref}
                  className="text-sm text-gray-400 transition-colors hover:text-brand-pink"
                >
                  {indiaSupport.phoneDisplay}
                </a>
              </li>

              <li className="flex items-center gap-3">
                <FaEnvelope className="text-brand-pink" />

                <a
                  href={indiaSupport.emailHref}
                  className="text-sm text-gray-400 transition-colors hover:text-brand-pink"
                >
                  {indiaSupport.email}
                </a>
              </li>

              <li className="flex items-center gap-3">
                <FaClock className="text-brand-pink" />
                <span className="text-sm text-gray-400">
                  {t("24/7 Customer Assistance")}
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-6 text-sm font-semibold uppercase tracking-wider text-white">
              {t("Newsletter")}
            </h4>

            <p className="mb-4 text-sm text-gray-400">
              {t("Subscribe to receive exclusive offers and travel inspiration.")}
            </p>

            <form className="flex flex-col gap-3">
              <input
                type="email"
                placeholder={t("Your email address")}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white transition-colors focus:border-brand-pink focus:outline-none"
              />

              <button
                type="button"
                className="w-full rounded-lg bg-brand-pink px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-brand-pink/90"
              >
                {t("Subscribe")}
              </button>
            </form>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-sm text-gray-500">
            {t("Copyright 2026 GET2VACATIONS. All rights reserved.")}
          </p>

          <div className="flex gap-6">
            <a
              href="#"
              className="text-sm text-gray-500 transition-colors hover:text-white"
            >
              {t("Privacy Policy")}
            </a>

            <a
              href="#"
              className="text-sm text-gray-500 transition-colors hover:text-white"
            >
              {t("Terms of Service")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
